import { useState } from "react"
import { AdminLayout } from "@/components/Admin/AdminLayout"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Link, router } from "@inertiajs/react"
import { Search, Download, ChevronLeft, ChevronRight, CheckCircle, XCircle } from "lucide-react"

interface UserItem {
  id: number
  email: string
  name: string
  confirmed: boolean
  created_at: string
  orders_count: number
  has_advanced_test_access: boolean
  has_test_result: boolean
}

interface Filters {
  date_from?: string | null
  date_to?: string | null
  search?: string | null
  has_orders?: string | null
  confirmed?: string | null
}

interface Pagination {
  page: number
  per_page: number
  total_count: number
  total_pages: number
}

interface Props {
  users: UserItem[]
  filters: Filters
  pagination: Pagination
  stats: {
    total: number
    confirmed: number
    with_orders: number
    with_paid_orders: number
  }
}

const formatDate = (value: string) => {
  const date = new Date(value)
  return new Intl.DateTimeFormat("pt-BR", { dateStyle: "short" }).format(date)
}

export default function UsersIndex({ users, filters, pagination, stats }: Props) {
  const [searchValue, setSearchValue] = useState(filters.search || "")

  const applyFilters = (newFilters: Partial<Filters>) => {
    const mergedFilters = {
      date_from: filters.date_from,
      date_to: filters.date_to,
      search: filters.search,
      has_orders: filters.has_orders,
      confirmed: filters.confirmed,
      ...newFilters,
      page: 1, // Reset to first page on filter change
    }

    // Remove empty values
    const cleanFilters = Object.fromEntries(
      Object.entries(mergedFilters).filter(([_, v]) => v !== null && v !== undefined && v !== "")
    )

    router.get("/admin/users", cleanFilters, { preserveScroll: true })
  }

  const goToPage = (page: number) => {
    if (page < 1 || page > pagination.total_pages) return
    router.get(
      "/admin/users",
      {
        ...filters,
        page,
      },
      { preserveScroll: true }
    )
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    applyFilters({ search: searchValue || null })
  }

  const exportCsv = () => {
    const params = new URLSearchParams()
    if (filters.date_from) params.append("date_from", filters.date_from)
    if (filters.date_to) params.append("date_to", filters.date_to)
    if (filters.search) params.append("search", filters.search)
    if (filters.has_orders) params.append("has_orders", filters.has_orders)
    if (filters.confirmed) params.append("confirmed", filters.confirmed)

    window.location.href = `/admin/users.csv?${params.toString()}`
  }

  return (
    <AdminLayout title="Usuarios">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <p className="text-sm text-muted-foreground">Total</p>
          <p className="text-2xl font-bold">{stats.total}</p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-muted-foreground">Confirmados</p>
          <p className="text-2xl font-bold text-green-600">{stats.confirmed}</p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-muted-foreground">Com pedidos</p>
          <p className="text-2xl font-bold">{stats.with_orders}</p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-muted-foreground">Com pedidos pagos</p>
          <p className="text-2xl font-bold text-blue-600">{stats.with_paid_orders}</p>
        </Card>
      </div>

      <Card className="p-4 md:p-6">
        {/* Filters Row */}
        <div className="flex flex-col gap-4 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <h2 className="text-lg font-semibold">Lista de usuários</h2>
            <Button variant="outline" size="sm" onClick={exportCsv}>
              <Download className="h-4 w-4 mr-2" />
              Exportar CSV
            </Button>
          </div>

          <div className="flex flex-col md:flex-row gap-4 flex-wrap">
            {/* Search */}
            <form onSubmit={handleSearch} className="flex gap-2 flex-1 max-w-md">
              <Input
                type="text"
                placeholder="Buscar por nome ou email..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
              />
              <Button type="submit" variant="outline" size="icon">
                <Search className="h-4 w-4" />
              </Button>
            </form>

            {/* Has Orders Filter */}
            <select
              value={filters.has_orders || ""}
              onChange={(event) => applyFilters({ has_orders: event.target.value || null })}
              className="border rounded-md px-3 py-2 text-sm bg-white"
            >
              <option value="">Todos os pedidos</option>
              <option value="true">Com pedidos</option>
              <option value="false">Sem pedidos</option>
            </select>

            {/* Confirmed Filter */}
            <select
              value={filters.confirmed || ""}
              onChange={(event) => applyFilters({ confirmed: event.target.value || null })}
              className="border rounded-md px-3 py-2 text-sm bg-white"
            >
              <option value="">Todos</option>
              <option value="true">Confirmados</option>
              <option value="false">Não confirmados</option>
            </select>

            {/* Date From */}
            <div className="flex items-center gap-2">
              <label className="text-sm text-muted-foreground whitespace-nowrap">De:</label>
              <Input
                type="date"
                value={filters.date_from || ""}
                onChange={(e) => applyFilters({ date_from: e.target.value || null })}
                className="w-auto"
              />
            </div>

            {/* Date To */}
            <div className="flex items-center gap-2">
              <label className="text-sm text-muted-foreground whitespace-nowrap">Até:</label>
              <Input
                type="date"
                value={filters.date_to || ""}
                onChange={(e) => applyFilters({ date_to: e.target.value || null })}
                className="w-auto"
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground border-b">
                <th className="py-2 pr-4">Nome</th>
                <th className="py-2 pr-4">Email</th>
                <th className="py-2 pr-4">Confirmado</th>
                <th className="py-2 pr-4">Criado</th>
                <th className="py-2 pr-4">Pedidos</th>
                <th className="py-2 pr-4">Teste avançado</th>
                <th className="py-2">Ações</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-muted-foreground">
                    Nenhum usuário encontrado
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id} className="border-b last:border-0">
                    <td className="py-2 pr-4">
                      <div className="font-medium text-foreground">{user.name || "-"}</div>
                      <div className="text-xs text-muted-foreground">
                        {user.has_test_result ? "Resultado ok" : "Sem resultado"}
                      </div>
                    </td>
                    <td className="py-2 pr-4">{user.email}</td>
                    <td className="py-2 pr-4">
                      {user.confirmed ? (
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      ) : (
                        <XCircle className="h-5 w-5 text-gray-400" />
                      )}
                    </td>
                    <td className="py-2 pr-4">{formatDate(user.created_at)}</td>
                    <td className="py-2 pr-4">{user.orders_count}</td>
                    <td className="py-2 pr-4">
                      {user.has_advanced_test_access ? (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Sim
                        </span>
                      ) : (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                          Não
                        </span>
                      )}
                    </td>
                    <td className="py-2">
                      <Link
                        href={`/admin/users/${user.id}`}
                        className="text-primary hover:underline"
                      >
                        Ver detalhes
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-between mt-6 pt-4 border-t">
            <p className="text-sm text-muted-foreground">
              Mostrando {(pagination.page - 1) * pagination.per_page + 1} a{" "}
              {Math.min(pagination.page * pagination.per_page, pagination.total_count)} de{" "}
              {pagination.total_count} usuários
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => goToPage(pagination.page - 1)}
                disabled={pagination.page === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm">
                Página {pagination.page} de {pagination.total_pages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => goToPage(pagination.page + 1)}
                disabled={pagination.page === pagination.total_pages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </AdminLayout>
  )
}
